import React, { useState } from 'react';
import {
  Col,
  Card,
  CardHeader,
  CardBody,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Row,
} from 'reactstrap';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiEyeOutline, mdiCloudArrowDownOutline } from '@mdi/js';
import { useQuery, useMutation } from '@tanstack/react-query';
import fileImg from '../../../assets/images/file-doc-img.png';
import { iconsDocuments } from '../../../assets/index';

import {
  fetchEmployeeDetails,
  fetchDocumentDetails,
} from '../../../store/apis/employeeService';
import '../index.scss';

function Documents() {
  const [previewDocumentModal, setPreviewDocumentModal] = useState(null);
  const [isDownloadLoading, setIsDownloadLoading] = useState('');
  const [isViewLoading, setIsViewLoading] = useState('');
  const [file, setFile] = useState(null);

  function getFileName(filePath) {
    return filePath.split('\\').pop();
  }

  const { data: personalDocuments, isLoading: personalDocumentsLoading } =
    useQuery({
      queryKey: ['employeeDocuments'],
      queryFn: () => fetchEmployeeDetails({ type: 'DOCUMENTS' }),
    });

  const { data: personalAttachments, isLoading: personalAttachmentsLoading } =
    useQuery({
      queryKey: ['employeeAttachments'],
      queryFn: () => fetchEmployeeDetails({ type: 'ATTACHMENTS' }),
    });

  const { mutate: previewDocument, isLoading: previewLoading } = useMutation({
    mutationFn: ({ fileName }) => fetchDocumentDetails({ fileName }),
    onSuccess: data => {
      setIsViewLoading('');
      const url = URL.createObjectURL(data);
      setFile(url);
      setPreviewDocumentModal(true);
    },
    onError: () => {
      setIsViewLoading('');
      toast.error('Failed to load document preview.', {
        className: 'toast-error',
      });
    },
  });

  const { mutate: downloadPdf } = useMutation({
    mutationFn: ({ fileName }) => fetchDocumentDetails({ fileName }),
    onSuccess: (data, variables) => {
      setIsDownloadLoading('');
      const url = window.URL.createObjectURL(
        new Blob([data], { type: 'application/pdf' }),
      );

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getFileName(variables.fileName)); // Use variables.fileName
      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link); // Cleanup
      window.URL.revokeObjectURL(url); // Free memory
    },
    onError: () => {
      setIsDownloadLoading('');
      toast.error('Failed to download document.', {
        className: 'toast-error',
      });
    },
  });

  const handleDownload = fileName => {
    setIsDownloadLoading(fileName);
    downloadPdf({ fileName });
  };

  const handleViewPDF = fileName => {
    setIsViewLoading(fileName);
    previewDocument({ fileName });
  };

  return (
    <div className="profile-component p-3">
      <Row>
        <Col>
          <Card style={{ border: '1px solid #98A2B3', borderRadius: '16px' }}>
            <CardHeader
              className="user-profile-header"
              style={{ borderRadius: '16px 16px 0 0' }}
            >
              <div className="user-profile-header-icon">
                <img src={iconsDocuments} alt="Documents Icon" />
              </div>
              <div>
                <span className="fw-semibold fs-5">Personal Documents</span>
              </div>
            </CardHeader>
            <CardBody>
              {/* Loading */}
              {personalDocumentsLoading && (
                <Spinner color="primary" size="sm" type="grow" />
              )}

              {personalDocuments?.map(document => (
                <div key={document?.['Doc.No']} className="document-item">
                  <Col lg={1} style={{ alignSelf: 'flex-start' }}>
                    <img
                      src={fileImg}
                      alt="Document preview"
                      height="30px"
                    />{' '}
                  </Col>
                  <Col lg={11}>
                    <div className="document-details">
                      <div className="d-flex flex-row align-items-center gap-2">
                        <div>
                          <span className=".personal-doc-subheader">
                            {document['Doc.No']}
                          </span>
                        </div>
                        <div
                          className="d-flex align-items-center justify-content-center rounded"
                          style={{
                            backgroundColor: '#ffffff',
                            border: '3px solid #98A2B3',
                            padding: '2px 6px',
                          }}
                        >
                          <span style={{ color: '#000', fontWeight: '500' }}>
                            {document?.DocumentType}
                          </span>
                        </div>
                      </div>

                      <div className="d-flex flex-row align-items-center gap-2">
                        <div className="d-flex flex-row align-items-center gap-1">
                          <span className="personal-doc-title">
                            Issued Date
                          </span>
                          <span className="personal-doc-subtitle">
                            {document?.IssueDate}
                          </span>
                        </div>
                        <div
                          style={{
                            height: '15px',
                            width: '1px',
                            borderLeft: '1px solid #475467',
                          }}
                        />
                        <div className="d-flex flex-row align-items-center gap-1">
                          <span className="personal-doc-title">
                            Expiry Date
                          </span>
                          <span className="personal-doc-subtitle">
                            {document?.ExpiryDate}
                          </span>
                        </div>
                        <div
                          style={{
                            height: '15px',
                            width: '1px',
                            borderLeft: '1px solid #475467',
                          }}
                        />
                        <div className="d-flex flex-row align-items-center gap-1">
                          <span className="personal-doc-title">
                            Days Due for Expiry
                          </span>
                          <span className="personal-doc-subtitle">
                            {document?.DaysDueforExpiry}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </div>
              ))}
            </CardBody>
          </Card>
        </Col>
        <Col>
          <Card style={{ border: '1px solid #98A2B3', borderRadius: '16px' }}>
            <CardHeader
              className="user-profile-header"
              style={{ borderRadius: '16px 16px 0 0' }}
            >
              <div className="user-profile-header-icon">
                <img src={iconsDocuments} alt="Documents Icon" />
              </div>
              <div>
                <span className="fw-semibold fs-5">Attachments</span>
              </div>
            </CardHeader>
            <CardBody>
              {personalAttachmentsLoading && (
                <Spinner color="primary" size="sm" type="grow" />
              )}
              {personalAttachments?.map(attachment => (
                <div key={attachment?.FileName} className="document-item">
                  <Col lg={1} style={{ alignSelf: 'flex-start' }}>
                    <img src={fileImg} alt="Document preview" height="30px" />
                  </Col>
                  <Col lg={8}>
                    <div className="document-details">
                      <div className="d-flex flex-row align-items-center">
                        <div
                          className="d-flex align-items-center justify-content-center rounded"
                          style={{
                            backgroundColor: '#fff',
                            border: '3px solid #98A2B3',
                            padding: '2px 6px',
                          }}
                        >
                          <span style={{ color: '#000', fontWeight: '500' }}>
                            {attachment?.Document}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span className="personal-doc-subheader">
                          {attachment?.FileName}
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col lg={3} style={{ alignSelf: 'flex-start' }}>
                    <div className="d-flex flex-row align-items-center gap-2 justify-content-end">
                      <button
                        type="button"
                        className="non-btn btn btn-link"
                        onClick={() => handleViewPDF(attachment?.Attachment)}
                        title="View Document"
                      >
                        {isViewLoading !== attachment?.Attachment && (
                          <Icon path={mdiEyeOutline} color="#017EBD" size={1} />
                        )}
                        {isViewLoading === attachment?.Attachment && (
                          <Spinner size="sm" color="primary" />
                        )}
                      </button>
                      <button
                        type="button"
                        className="non-btn btn btn-link"
                        onClick={() => handleDownload(attachment?.Attachment)}
                        title="Download Document"
                      >
                        {isDownloadLoading !== attachment?.Attachment && (
                          <Icon
                            path={mdiCloudArrowDownOutline}
                            color="#017EBD"
                            size={1}
                          />
                        )}
                        {isDownloadLoading === attachment?.Attachment && (
                          <Spinner size="sm" color="primary" />
                        )}
                      </button>
                    </div>
                  </Col>
                </div>
              ))}
            </CardBody>
          </Card>
          <Modal
            isOpen={previewDocumentModal}
            toggle={() => setPreviewDocumentModal(false)}
            centered
            size="xl"
            fullscreen
          >
            <div
              style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <ModalHeader toggle={() => setPreviewDocumentModal(false)}>
                Preview Document
              </ModalHeader>
              <ModalBody
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {previewLoading && (
                  <div className="d-flex justify-content-center">
                    <Spinner color="primary" size="sm" type="grow" />
                  </div>
                )}
                <div
                  style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <iframe
                    src={file}
                    title="Document Preview"
                    style={{
                      flex: 1,
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                  />
                </div>
              </ModalBody>
            </div>
          </Modal>
        </Col>
      </Row>
    </div>
  );
}

export default Documents;
